import * as React from 'react'
import {useCallback, useMemo} from 'react'

import BuildLineLayout from '../../../../containers/BuildLineLayout'
import {PlaceIdList} from '../../../../plugins/plugins.types'
import type {ProjectId} from '../../../../types'
import {UserProperties} from '../../../../utils/userProperties'
import ModeSwitch, {Mode, useMode} from '../../../common/ModeSwitch/ModeSwitch'
import NestedSubprojectsAndBuildTypes from '../../../common/NestedSubprojectsAndBuildTypes/NestedSubprojectsAndBuildTypes'
import {ContentContext} from '../../../common/NestedSubprojectsAndBuildTypes/NestedSubprojectsAndBuildTypesContext'
import Plugin from '../../../common/Plugin/Plugin'
import ProjectOverviewToolBar from '../../../common/ProjectToolbar/ProjectToolBar'
import {
  ResetLoadingStateProvider,
  useSequenceLoaderReadyHandler,
} from '../../../common/SequenceLoader/SequenceLoader.hooks'
import {trendsOrBuildsHint} from '../../../packages/Hints/HintItems/Hints.projectOverview'

import BuildsByBuildType from './BuildsByBuildType/BuildsByBuildType'
import BuildTypesInfo from './BuildTypesInfo/BuildTypesInfo'
import {Modes} from './NestedProjectTrendsOrBuildsOverview.modes'

import styles from './ProjectOverviewTab.css'

type OwnProps = {
  projectId: ProjectId
  hasBuildTypes?: boolean
  level?: number
  onlyFavorites?: boolean
  projectsReady: boolean
  hasPermissionToAdd?: boolean
  showAll?: boolean
}

const NestedProjectTrendsOrBuildsOverview = ({
  projectId,
  hasBuildTypes = false,
  onlyFavorites = false,
  projectsReady,
  level = 0,
  showAll,
}: OwnProps) => {
  const viewMode = useMode(Modes.BUILDS)
  const nested = level > 0
  const isTrendsView = viewMode === Modes.TRENDS
  useSequenceLoaderReadyHandler(viewMode !== Modes.BUILDS)

  const nestedRender = useCallback(
    () =>
      isTrendsView ? (
        <BuildTypesInfo showAll={showAll} projectId={projectId} onlyFavorites={onlyFavorites} />
      ) : (
        <BuildLineLayout showAgent showExactDuration={false} showPath={false}>
          <BuildsByBuildType
            projectId={projectId}
            onlyFavorites={onlyFavorites}
            nested={level > 0}
          />
        </BuildLineLayout>
      ),
    [isTrendsView, level, onlyFavorites, projectId, showAll],
  )

  return useMemo(
    () => (
      <ResetLoadingStateProvider>
        {!nested && (
          <>
            <ModeSwitch
              className={styles.toggleContainer}
              preserveParams={({projectTab, branch}) => ({
                projectTab,
                branch,
              })}
              userProperty={UserProperties.PROJECT_OVERVIEW_MODE}
              customItems={
                <ProjectOverviewToolBar projectId={projectId} onlyFavorites={onlyFavorites} />
              }
              hintId={trendsOrBuildsHint.id}
            >
              <Mode mode={Modes.BUILDS} label="Builds" />
              <Mode mode={Modes.TRENDS} label="Trends" />
            </ModeSwitch>
            {isTrendsView ? (
              <Plugin placeId={PlaceIdList.SAKURA_PROJECT_TRENDS} />
            ) : (
              <Plugin placeId={PlaceIdList.SAKURA_PROJECT_BUILDS} />
            )}
          </>
        )}
        <ContentContext.Provider value={nestedRender}>
          <NestedSubprojectsAndBuildTypes
            projectId={projectId}
            onlyFavorites={onlyFavorites}
            hasSubprojectContent={hasBuildTypes}
            projectsReady={projectsReady}
            level={level}
            limitWidth={!isTrendsView}
          />
        </ContentContext.Provider>
      </ResetLoadingStateProvider>
    ),
    [
      hasBuildTypes,
      isTrendsView,
      level,
      nested,
      nestedRender,
      onlyFavorites,
      projectId,
      projectsReady,
    ],
  )
}

export default NestedProjectTrendsOrBuildsOverview
